import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  MeasurementUnitEnum,
  MeasureType,
} from 'domains/measurementDomain';
import {
  basisWeightAbbrev,
  formatConvertedMeasure,
  toShown,
  isMetric,
  unitAbbrev,
  unitSymbol, inViewerUnit, measureTypeForField, reexpress, roundSizeForDisplay } from 'utils/measurement';
import { useMeasurementUnitScope } from './useMeasurementUnitScope';

/**
 * The measurement system being read in, plus the conversions bound to it.
 *
 * It comes from the active org - its own setting, else its country - and the header menu lets a
 * reader hold it at the other system for as long as they are looking. It is a viewing choice
 * only: which system a *record* belongs to is decided by the org that owns it, on the API side,
 * and no amount of switching here changes what a save writes.
 *
 * An earlier design let each form carry its own selector, so a person could choose the unit a
 * submission was in. That made the unit a property of whoever happened to be filling the form -
 * the same SKU entered in millimetres by one colleague and inches by the next - which is the
 * thing the org's country now settles once.
 */
export const useMeasurementUnit = () => {
  const dispatch = useAppDispatch();
  const scope = useMeasurementUnitScope();
  const readerUnit = useAppSelector(
    (state) => state.app.measurementUnit ?? MeasurementUnitEnum.IMPERIAL
  );
  // The form's own selector when there is one, the reader's setting otherwise.
  const unit = scope?.unit ?? readerUnit;
  const setScopeUnit = scope?.setUnit;

  const switchUnit = useCallback(
    (next: MeasurementUnitEnum) => {
      if (setScopeUnit) {
        setScopeUnit(next);
        return;
      }
      dispatch.app.switchMeasurementUnit(next);
    },
    [dispatch, setScopeUnit]
  );

  return useMemo(
    () => ({
      unit,
      metric: isMetric(unit),
      switchUnit,
      /** True when a form's own selector decides the unit, rather than the reader's setting. */
      scoped: setScopeUnit !== undefined,
      /**
       * Declares that this payload's numbers are in the unit the form was displaying.
       *
       * Only for a form that actually re-expresses its values - one built from
       * `inViewerUnit`, `useRefillOnUnitSwitch` or `useCustomSizeUnit`, whose fields hold
       * millimetres when the page is showing millimetres. Those are the only payloads whose
       * numbers are not already base.
       *
       * Never add it to a payload that is handing a stored record back. `utils/api` used to
       * declare the viewer's unit on every write, including those, and the org form's untouched
       * 0.125in bleed was saved as 0.125mm because of it. Silence means "already base", which is
       * what a stored value is.
       */
      tag: <T extends object>(payload: T) => ({ ...payload, enteredUnit: unit }),
      /** Tight symbol that sits after a number, e.g. `mm` or `"`. */
      symbol: (type: MeasureType) => unitSymbol(type, unit),
      /** Spelled unit for a label, e.g. `in` or `mm`. */
      abbrev: (type: MeasureType) => unitAbbrev(type, unit),
      /**
       * A column header naming its unit, e.g. `Width (mm)`.
       *
       * Tables put the unit here and leave the cells as bare numbers - repeating `mm` down every
       * row of a long table is noise, and the numbers stop lining up.
       */
      columnTitle: (label: string, type: MeasureType) =>
        `${label} (${unitAbbrev(type, unit)})`,
      /**
       * Picks between the two values the API sent for one measurement.
       *
       * The API sends both units - `widthIn` and `widthMm` - precisely so switching units is
       * instant: nothing refetches when the user switches, so a single value chosen by the server
       * would freeze while its label changed. Nothing is converted here, only chosen.
       */
      display: (
        // Numbers only, deliberately. This measures - it rounds to the metric display precision
        // and returns a number - so a label that is already text, such as `100lb` or
        // `17x11x3 in`, came back from Number() as NaN and rendered as "undefined". Pick those
        // with `basisWeight`, `sizeFor` or `optionValue` instead.
        imperial: number | null | undefined,
        options?: { metric?: number | null }
      ) =>
        toShown(
          isMetric(unit) ? options?.metric ?? imperial : imperial,
          isMetric(unit)
        ),
      /** Same, with the unit symbol appended, e.g. `215.9mm`. */
      format: (
        imperial: number | string | null | undefined,
        type: MeasureType,
        options?: { withSymbol?: boolean; metric?: number | string | null }
      ) =>
        formatConvertedMeasure(
          isMetric(unit) ? options?.metric ?? imperial : imperial,
          type,
          unit,
          options
        ),
      /**
       * What the user typed, sent exactly as typed.
       *
       * Kept as a seam for callers that read as "convert before sending"; the API now decides
       * which column each side belongs in, so there is nothing to convert.
       */
      toStored: (value: number | string | null | undefined, _type: MeasureType) =>
        value === null || value === '' ? undefined : Number(value),
      /**
       * Labels a basis weight the API already converted.
       *
       * Pass the `basisWeightShown` field, never the raw `basisWeight`: that one stays as the
       * supplier typed it because the paper picker matches its options on it.
       *
       * The unit is appended only when the API actually converted - a value it could not read
       * comes back as the supplier's own string, and calling that gsm would be a lie.
       */
      basisWeight: (
        imperial: string | number | null | undefined,
        // An object, not a second positional value: the old signature took the paper's category
        // here, and every call site kept compiling when the meaning changed - so a metric viewer
        // was shown "coated_text" where the weight belonged. Both are strings; only the shape
        // makes the mistake impossible.
        options: { metric: string | number | null | undefined; withUnit?: boolean }
      ) => {
        const { metric } = options;
        const picked = isMetric(unit) ? metric ?? imperial : imperial;
        if (picked === null || picked === undefined || picked === '') {
          return '';
        }
        const text = String(picked);
        if (options?.withUnit === false) {
          return text;
        }
        const converted = text !== String(imperial ?? '');
        return converted ? `${text} ${basisWeightAbbrev(unit)}` : text;
      },
      /**
       * A basis-weight column header - the label alone, with no unit.
       *
       * It used to append the reader's unit, which was a claim the column cannot make: every
       * value in it is shown exactly as the supplier supplied it, so one column holds `100lb`
       * and `350gsm` at once. Heading that "(gsm)" mislabels half the rows, and heading it
       * "(lb)" mislabels the other half. Each value carries its own unit in the string.
       */
      basisWeightTitle: (label: string) => label,
      /**
       * Labels a service-option value using what the API already converted.
       *
       * The API sends `valuesShown`, a map from each raw value to its label, and only for options
       * tagged as measurements. The caller must keep using the raw string as the option's value -
       * it is what the buyer submits and what the option is matched on.
       */
      optionValue: (
        value: string,
        valuesMetric: Record<string, string> | null | undefined
      ): string => (isMetric(unit) ? valuesMetric?.[value] ?? value : value),
      /**
       * Labels a size string the API already converted, e.g. `215.9x279.4 mm`.
       *
       * Pass the `...Shown` field, never the raw `finishedSize`/`finishedSizeIn`: those stay
       * imperial because the size picker matches a saved selection on them and posts them back,
       * and this no longer converts, so a raw value would be labelled with the wrong unit.
       *
       * Carries its unit by default: these land in free-form labels where nothing else says what
       * the numbers are. Pass `withUnit: false` only for a table cell whose header names the unit.
       */
      displaySize: (
        imperial: string | null | undefined,
        // Required, not optional: a caller that forgets the companion silently labels the
        // imperial size "mm" - exactly the bug this pair exists to prevent.
        options: { withUnit?: boolean; metric?: string | null }
      ) => {
        // Shortened here, not upstream: the derived side of a size carries six decimals because
        // the pricing engine works from it, and only a reader needs it cut down.
        const shown = roundSizeForDisplay(
          (isMetric(unit) ? options?.metric ?? imperial : imperial) ?? undefined,
          isMetric(unit)
        );
        if (!shown || options?.withUnit === false) {
          return shown;
        }
        return `${shown} ${unitAbbrev(MeasureType.LENGTH, unit)}`;
      },
      /**
       * Picks between the two size strings the API sent, without labelling it.
       *
       * For callers that take the size apart - `organizeStructure` and friends - rather than
       * print it.
       */
      sizeFor: (
        imperial: string | null | undefined,
        metric?: string | null
      ): string | undefined =>
        (isMetric(unit) ? metric ?? imperial : imperial) ?? undefined,

      /**
       * An edited size string, sent exactly as typed.
       *
       * Kept as a seam for callers that read as "convert before sending". Sizes are posted back
       * in the unit they were entered in and the API decides what to do with them - converting
       * here would round-trip the value through display precision and change it.
       */
      sizeToStored: (size: string | null | undefined) => size,
    }),
    [unit, switchUnit, setScopeUnit]
  );
};

/**
 * Binds one imperial form field to the viewer's measurement system.
 *
 * Spread the result onto any controlled number input - antd `InputNumber`, `RevxInputNumber`, a
 * plain `input` - and the form keeps holding imperial while the user sees and types their own unit:
 *
 * ```tsx
 * <Form.Item name="widthIn">
 *   <RevxInputNumber {...useMeasureField(MeasureType.LENGTH)} />
 * </Form.Item>
 * ```
 *
 * Two properties matter and both are load-bearing:
 *
 * - An untouched field never fires `onChange`, so it submits the value it loaded, byte for byte.
 *   Round-tripping every field through a 2-decimal metric display and back would quietly rewrite
 *   the whole record on each save.
 * - An edited field converts back at full precision, not display precision: 210mm is stored as
 *   8.267716535433072", never as 8.27".
 */
export const useMeasureField = (
  type: MeasureType,
  props?: {
    value?: number | string | null;
    onChange?: (value: number | undefined) => void;
  }
) => {
  const [typed, setTyped] = useState<number | string | null | undefined>();
  const [editing, setEditing] = useState(false);
  const { value, onChange } = props ?? {};

  // A change from outside (form reset, record switch, unit switch) wins over a stale draft.
  useEffect(() => {
    if (!editing) {
      setTyped(undefined);
    }
  }, [value, editing]);

  return {
    // The API speaks the viewer's unit in both directions, so the value arrives ready to show.
    value: typed !== undefined ? typed : (value ?? undefined),
    onFocus: () => setEditing(true),
    onBlur: () => {
      setEditing(false);
      setTyped(undefined);
    },
    onChange: (next: number | string | null) => {
      // A comma is how most of the world writes a decimal point, and these fields are read in
      // millimetres by the people most likely to use one. `Number('3,5')` is NaN, so it used to
      // reach the form as NaN and the value simply vanished as it was typed, with no message.
      const normalised =
        typeof next === 'string' ? next.replace(',', '.') : next;
      setTyped(normalised);
      // Send exactly what was typed. Converting here and again on the way back is what turned an
      // entered 888mm into 888.01mm; the backend now stores the entered unit verbatim and derives
      // the other one, so the number must reach it untouched.
      onChange?.(
        normalised === null || normalised === '' ? undefined : Number(normalised)
      );
    },
  };
};

/**
 * Refills a form's measurement fields when the viewer switches units.
 *
 * An antd form holds numbers, not units, and `initialValues` only applies on mount - so without
 * this the values stay in the unit they were loaded in while the column headers flip. Saving in
 * that state is what divided a SKU's sizes by 25.4.
 *
 * Only the measurement fields are rewritten, so anything else the user has typed survives the
 * switch - and so does an edit to a measurement field. A number whose unit just changed cannot
 * be left under the new label, but it can be re-expressed, and that is what the user meant. The
 * record's own companion is only reached for when nobody has touched the field, because there
 * the stored value is both more precise and still what the box is showing.
 */
export const useRefillOnUnitSwitch = <T extends Record<string, unknown>>(
  form: {
    setFieldsValue: (values: Record<string, unknown>) => void;
    getFieldsValue: () => Record<string, unknown>;
    isFieldTouched: (name: string) => boolean;
  },
  record: T | undefined | null
) => {
  const { unit } = useMeasurementUnit();
  const filledIn = useRef(unit);

  useEffect(() => {
    const from = filledIn.current;
    if (from === unit) {
      return;
    }
    filledIn.current = unit;

    const shown = inViewerUnit(record, unit);
    const current = form.getFieldsValue() ?? {};
    const measurements: Record<string, unknown> = {};

    Object.keys(current).forEach((field) => {
      const type = measureTypeForField(field);
      if (!type) {
        return;
      }
      // The record's own companion first, when it has one and nobody has edited the field: the
      // API derived it from the stored base value at full precision, so switching back and
      // forth lands on the same number every time. Converting the displayed value instead would
      // compound its rounding on each switch.
      //
      // The touched check is what keeps an edit. Without it, typing a new width and then
      // switching units put the record's old width back - the one number on the form the user
      // had actually changed was the one the switch threw away.
      if (
        !form.isFieldTouched(field) &&
        shown &&
        field in shown &&
        shown[field] !== null &&
        shown[field] !== undefined
      ) {
        measurements[field] = shown[field];
        return;
      }
      // Either the user typed this one, or there is nothing to fall back on - a create form, or
      // a field this record does not carry. Re-express what is in the box: it was typed in the
      // unit the form was showing, and leaving it under the new unit's label is what saved an
      // 8.5in width as 8.5mm.
      const value = current[field];
      // Numbers only. A field can hold a shape rather than a value - the filter panels bind
      // `widthIn` to `{operator, values}` - and Number() of an object is NaN, which would wipe
      // the field on the first switch. Those have their own hook below.
      if (typeof value !== 'number' && typeof value !== 'string') {
        return;
      }
      if (value !== '' && Number.isFinite(Number(value))) {
        measurements[field] = reexpress(value, type, from, unit);
      }
    });

    if (Object.keys(measurements).length) {
      form.setFieldsValue(measurements);
      // Deliberately left marked as touched. Clearing it would make the precision rule ignore
      // these - which is what we want - but `omitUntouchedMeasurements` reads the same flag to
      // decide what to send, and it would then drop the value the user had just entered.
      // The two ask different questions of one flag; the save is the one that must not be wrong.
    }
  }, [unit, record, form]);
};

/**
 * Drops the measurement fields the user did not touch.
 *
 * These forms submit `{...record, ...formValues}`, so every measurement goes back whether or not
 * anyone looked at it. From a metric form that means a stored imperial value makes a round trip
 * it did not need - 0.0625in is shown as 1.588mm, and 1.588mm comes back as 0.062520in. The value
 * drifts a little on every save, forever.
 *
 * The API leaves out what a request does not mention (`MeasurementFields.store` ignores a null),
 * so the fix is simply not to mention it. Fields the user did edit are sent as normal.
 *
 * Only measurement fields are dropped; everything else in the payload is untouched.
 *
 * **Updates only.** On a create there is nothing stored to leave alone, so a field nobody typed
 * in is not "unchanged" - it is absent. "Save as new" and "copy from base" both build their
 * payload from a record the user only glanced at, and dropping the untouched measurements there
 * created a parcel with no width, length, height or weight and a material that failed its
 * @NotNull checks. Keyed on `id`, which is what every caller already uses to tell the two apart.
 */
export const omitUntouchedMeasurements = <T extends Record<string, unknown>>(
  payload: T,
  form: { isFieldTouched: (name: string) => boolean }
): T => {
  if (payload.id === undefined || payload.id === null) {
    return payload;
  }
  const kept: Record<string, unknown> = {};
  Object.keys(payload).forEach((field) => {
    if (measureTypeForField(field) && !form.isFieldTouched(field)) {
      return;
    }
    kept[field] = payload[field];
  });
  return kept as T;
};

/**
 * The filter-panel flavour of {@link useRefillOnUnitSwitch}.
 *
 * A filter panel binds each criterion to `{operator, values}` rather than to a bare number, so
 * the numbers sit one level down and in a list. Otherwise the reason is identical: a width filter
 * of 8.5 typed while the panel read inches is 8.5mm the moment the reader switches, and it then
 * matches nothing at all - a silent empty result rather than a visible error.
 *
 * There is nothing to fall back on here. A filter is not a record, so the typed values are
 * re-expressed in place.
 */
export const useRefillFilterOnUnitSwitch = (form: {
  setFieldsValue: (values: Record<string, unknown>) => void;
  getFieldsValue: () => Record<string, unknown>;
}) => {
  const { unit } = useMeasurementUnit();
  const filledIn = useRef(unit);

  useEffect(() => {
    const from = filledIn.current;
    if (from === unit) {
      return;
    }
    filledIn.current = unit;

    const current = form.getFieldsValue() ?? {};
    const next: Record<string, unknown> = {};

    Object.keys(current).forEach((field) => {
      const type = measureTypeForField(field);
      const criterion = current[field] as { values?: unknown } | undefined;
      if (!type || !criterion || !Array.isArray(criterion.values)) {
        return;
      }
      const values = criterion.values.map((value) =>
        value === null || value === undefined || value === ''
          ? value
          : reexpress(value as number, type, from, unit)
      );
      next[field] = { ...criterion, values };
    });

    if (Object.keys(next).length) {
      form.setFieldsValue(next);
    }
  }, [unit, form]);
};

/**
 * Whether the org's measurement system is settled yet.
 *
 * A form that takes measurements should wait for this. The unit is resolved by an API call during
 * session hydration, and until it answers the page is showing a cached guess or imperial - so a
 * form opened in that window labels its fields one way and relabels them a moment later, with
 * whatever has been typed into them still sitting there under the new label.
 *
 * Read-only pages do not need it: relabelling a number nobody is editing is untidy, not wrong.
 */
export const useMeasurementUnitReady = () =>
  useAppSelector((state) => state.app.measurementUnitReady ?? false);
